import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api/v1';

// ============================
// SECURE CACHE — Scoped per user token
// ============================
const requestCache = new Map();
const CACHE_DURATION = 5000; // 5 seconds

// Get a user-scoped cache key
const getCacheKey = (config) => {
  const token = localStorage.getItem('token') || 'anonymous';
  const userScope = token.length > 16 ? token.slice(-16) : token;
  // Exclude _nc param from cache key
  const params = { ...(config.params || {}) };
  delete params._nc;
  return `${userScope}:${config.url}:${JSON.stringify(params)}`;
};

// Clear ALL cache — call on login, logout, token refresh
export const clearCache = () => {
  requestCache.clear();
  if (process.env.NODE_ENV === 'development') {
    console.log('🗑️ API cache cleared');
  }
};

// Invalidate cache — call after any mutation (POST, PATCH, DELETE)
const invalidateCache = () => {
  requestCache.clear();
};

// ============================
// AXIOS INSTANCE
// ============================
const API = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
  withCredentials: true, // Send HttpOnly auth cookies on every request
});

// ============================
// JWT EXPIRY DECODE (pre-emptive refresh)
// ============================
// Decode the `exp` claim of a JWT without verifying signature — we
// only need to know when the access token will expire so we can
// refresh BEFORE it does, instead of letting requests 401 and
// retrying. This eliminates the 401-then-retry pattern that polluted
// the browser console on every page navigation after a long session.
//
// Returns the unix-second `exp` value, or null if the token is
// malformed / missing.
const getTokenExpiry = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64 → JSON
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = JSON.parse(atob(padded));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch (_) {
    return null;
  }
};
// Refresh threshold — if the token will expire within this many
// seconds, refresh proactively before sending the request. 60s is
// generous enough to absorb clock skew between client and server.
const REFRESH_BUFFER_SECONDS = 60;

// ============================
// REQUEST INTERCEPTOR
// ============================
API.interceptors.request.use(
  async (config) => {
    // Attach access token from localStorage as Authorization header
    // (short-lived access token; the refresh token lives in an HttpOnly cookie only)
    let token = localStorage.getItem('token');

    // Pre-emptive refresh: if the current token is within
    // REFRESH_BUFFER_SECONDS of expiry, swap it for a fresh one
    // BEFORE this request goes out. Skipped for the refresh endpoint
    // itself (would deadlock) and for explicit-no-auth calls.
    if (
      token &&
      !config.skipAuth &&
      !config.url?.includes('/auth/refresh-token')
    ) {
      const exp = getTokenExpiry(token);
      const nowSec = Math.floor(Date.now() / 1000);
      if (exp !== null && exp - nowSec < REFRESH_BUFFER_SECONDS) {
        try {
          const fresh = await getFreshAccessToken();
          localStorage.setItem('token', fresh);
          token = fresh;
          // Re-auth the socket so it doesn't keep reconnecting with
          // the stale token (same fix as the response-interceptor path).
          try {
            const { refreshSocketAuth } = require('../hooks/useSocket');
            refreshSocketAuth(fresh);
          } catch (_) { /* optional */ }
        } catch (_) {
          // Refresh failed — fall through and let the request go with
          // the (probably-already-expired) token; the response
          // interceptor will handle the 401 + redirect-to-login.
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Cache GET requests only
    if (config.method === 'get') {
      const cacheKey = getCacheKey(config);
      const cached = requestCache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        config.adapter = () => {
          return Promise.resolve({
            data: cached.data,
            status: 200,
            statusText: 'OK (Cached)',
            headers: cached.headers || {},
            config,
            request: {}
          });
        };
        return config;
      }

      // Cache-bust only for actual server requests (skip notification polling)
      if (!config.url.includes('/notifications/unread-count')) {
        config.params = { ...config.params, _nc: Date.now() };
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================
// REFRESH SINGLETON
// ============================
// When many requests 401 at the same time (tab wake, parallel fetches on
// dashboard mount, etc.) they must NOT each fire their own /refresh-token.
// Refresh rotation invalidates the previous refresh cookie, so whichever
// request runs second sees a 400 and would kick the user out. Instead:
// share one in-flight refresh promise and have every concurrent 401 await it.
let refreshInFlight = null;
const getFreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_URL}/auth/refresh-token`, {}, { withCredentials: true })
      .then((res) => res.data.data.accessToken)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

// ============================
// RESPONSE INTERCEPTOR
// ============================
API.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && response.status === 200) {
      const cacheKey = getCacheKey(response.config);
      requestCache.set(cacheKey, {
        data: response.data,
        headers: response.headers,
        timestamp: Date.now()
      });

      // Evict oldest if cache grows too large
      if (requestCache.size > 50) {
        const oldestKey = requestCache.keys().next().value;
        requestCache.delete(oldestKey);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 — Token expired, try refresh via HttpOnly refreshToken cookie
    // Skip refresh for login/register calls (skipAuth flag) — those 401s are credential errors
    if (error.response?.status === 401 && !originalRequest?._retry && !originalRequest?.skipAuth) {
      originalRequest._retry = true;

      try {
        // refreshToken is in an HttpOnly cookie — sent automatically via withCredentials.
        // Singleton ensures concurrent 401s share one refresh call (see top of file).
        const accessToken = await getFreshAccessToken();

        // Store the new short-lived access token and clear stale cache
        localStorage.setItem('token', accessToken);
        clearCache();

        // Re-auth the realtime socket too — otherwise it keeps reconnecting
        // with the stale token and loops (which triggered /bookings refetches
        // that piled up and caused 429s on paths that weren't whitelisted).
        try {
          const { refreshSocketAuth } = require('../hooks/useSocket');
          refreshSocketAuth(accessToken);
        } catch (_) { /* optional */ }

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear session and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearCache();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 429 Rate Limit
    if (error.response?.status === 429) {
      toast.error('Too many requests. Please wait a moment.');
      return Promise.reject(error);
    }

    // Show error toast — skip 401 handled by refresh flow above, skip if caller handles its own toast.
    // Mutations (POST/PATCH/PUT/DELETE) are user-initiated and the calling component always renders
    // its own feedback, so we don't auto-toast them here (otherwise the user sees two messages for
    // a single failure). Auto-toast remains as a safety net for GET errors on pages that don't
    // explicitly handle them.
    const method = (originalRequest?.method || '').toLowerCase();
    const isMutation = method && method !== 'get';
    if (
      error.response &&
      error.response.status !== 401 &&
      !originalRequest?.skipToast &&
      !isMutation
    ) {
      const message = error.response.data?.message || 'An error occurred';
      if (message !== 'Too many requests. Please wait a moment.') {
        // Use the message itself as the toastId — react-toastify will
        // dedupe so two parallel requests that fail with the same error
        // (e.g. a page that fires 2 GETs in parallel) only show one toast.
        toast.error(message, { toastId: message });
      }
    }

    return Promise.reject(error);
  }
);

// ============================
// AUTH API
// ============================
export const authAPI = {
  register: (data) => API.post('/auth/register', data).then(res => { clearCache(); return res; }),
  login: (data) => API.post('/auth/login', data, { skipToast: true, skipAuth: true }).then(res => { clearCache(); return res; }),
  logout: () => API.post('/auth/logout').then(res => { clearCache(); return res; }),
  refreshToken: (data) => API.post('/auth/refresh-token', data),
  changePassword: (data) => API.post('/auth/change-password', data).then(res => { invalidateCache(); return res; }),
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  resetPassword: (data) => API.post('/auth/reset-password', data),
  verifyEmail: (token) => API.get(`/auth/verify-email/${token}`, { skipToast: true }),
  resendVerification: () => API.post('/auth/resend-verification'),
  resendVerificationPublic: (email) => API.post('/auth/resend-verification-public', { email }, { skipToast: true }),
  getMe: () => API.get('/auth/me'),
  getSessions: () => API.get('/auth/sessions'),
  revokeSession: (sessionId) => API.delete(`/auth/sessions/${sessionId}`),
  revokeAllSessions: () => API.delete('/auth/sessions'),
  revokeEverySession: () => API.post('/auth/sessions/revoke-every'),
  verifyPassword: (password) => API.post('/auth/verify-password', { password }, { skipToast: true }),
};

// ============================
// USER API
// ============================
export const userAPI = {
  getProfile: () => API.get('/users/profile'),
  updateProfile: (data) => API.patch('/users/profile', data).then(res => { invalidateCache(); return res; }),
  deleteAccount: (data) => API.delete('/users/account', { data }).then(res => { invalidateCache(); return res; }),
  getStats: () => API.get('/users/stats'),
  updateNotifications: (data) => API.patch('/users/preferences/notifications', data).then(res => { invalidateCache(); return res; }),
  getLoginHistory: () => API.get('/users/login-history'),
  revokeAllTokens: () => API.post('/users/revoke-tokens').then(res => { invalidateCache(); return res; }),
  updatePreferences: (data) => API.patch('/users/preferences/display', data).then(res => { invalidateCache(); return res; }),
  cleanupOrphans: () => API.post('/users/cleanup-orphans').then(res => { invalidateCache(); return res; }),
};

// ============================
// CALENDAR API
// ============================
export const calendarAPI = {
  getAll: (params) => API.get('/calendars', { params }),
  getById: (id) => API.get(`/calendars/${id}`),
  getBySlug: (slug) => API.get(`/calendars/slug/${slug}`),
  create: (data) => API.post('/calendars', data).then(res => { invalidateCache(); return res; }),
  update: (id, data) => API.patch(`/calendars/${id}`, data).then(res => { invalidateCache(); return res; }),
  delete: (id) => API.delete(`/calendars/${id}`).then(res => { invalidateCache(); return res; }),
  toggleStatus: (id) => API.patch(`/calendars/${id}/toggle-status`).then(res => { invalidateCache(); return res; }),
  updateSlug: (id, slug) => API.patch(`/calendars/${id}/slug`, { slug }).then(res => { invalidateCache(); return res; }),
  getEmbedCode: (id) => API.get(`/calendars/${id}/embed-code`),
  getAvailableSlots: (slug, params) => API.get(`/calendars/slug/${slug}/slots`, { params }),
  getRecentLocations: () => API.get('/calendars/recent-locations', { skipToast: true }),
  addDateOverride: (calendarId, data) => API.post(`/calendars/${calendarId}/date-override`, data),
  removeDateOverride: (calendarId, data) => API.delete(`/calendars/${calendarId}/date-override`, { data }),
  previewDateBlock: (calendarId, date) => API.get(`/calendars/${calendarId}/date-override/preview`, { params: { date } }),
  addEmbedDomain: (calendarId, data) => API.post(`/calendars/${calendarId}/embed-domain`, data),
  removeEmbedDomain: (calendarId, data) => API.delete(`/calendars/${calendarId}/embed-domain`, { data }),
};

// ============================
// BOOKING API
// ============================
export const bookingAPI = {
  create: (data) => API.post('/bookings', data).then(res => { invalidateCache(); return res; }),
  // Owner-initiated manual booking (phone bookings, walk-ins). Auth required.
  createManual: (data) => API.post('/bookings/manual', data).then(res => { invalidateCache(); return res; }),
  getAll: (params) => API.get('/bookings', { params }),
  // Silent variant for widgets — failure doesn't raise a toast
  getAllSilent: (params) => API.get('/bookings', { params, skipToast: true }),
  getUpcoming: (limit) => API.get('/bookings/upcoming', { params: { limit } }),
  getById: (id) => API.get(`/bookings/${id}`),
  getByReference: (reference) => API.get(`/bookings/reference/${reference}`),
  update: (id, data) => API.patch(`/bookings/${id}`, data).then(res => { invalidateCache(); return res; }),
  cancel: (id, data) => API.post(`/bookings/${id}/cancel`, data).then(res => { invalidateCache(); return res; }),
  // Cancel every upcoming session in a recurring package by source-booking id.
  // Falls back to single-session cancel if the booking has no recurringGroupId.
  cancelPackage: (id, data) => API.post(`/bookings/${id}/cancel-package`, data).then(res => { invalidateCache(); return res; }),
  confirm: (id) => API.post(`/bookings/${id}/confirm`).then(res => { invalidateCache(); return res; }),
  reschedule: (id, data) => API.post(`/bookings/${id}/reschedule`, data).then(res => { invalidateCache(); return res; }),
  markComplete: (id) => API.post(`/bookings/${id}/complete`).then(res => { invalidateCache(); return res; }),
  // Team-booking: reassign a booking to a different team member.
  reassign: (id, assignedTo) => API.patch(`/bookings/${id}/reassign`, { assignedTo }).then(res => { invalidateCache(); return res; }),
  // List of users (owner + active team members) who can be assigned bookings.
  // skipToast — used on the EditCalendar mount; if the user has no team,
  // a 500 here shouldn't pop a scary toast. The caller already handles
  // the empty / error states inline.
  getAssignableMembers: () => API.get('/bookings/team-members', { skipToast: true }),
  delete: (id) => API.delete(`/bookings/${id}`).then(res => { invalidateCache(); return res; }),
  // Update guest email / phone / internalNotes on an existing booking.
  // Used by the booking row "Edit" modal in the dashboard. The same
  // endpoint is invoked server-side by the agent's update_booking_guest
  // tool so UI + agent share validation.
  updateGuest: (id, data) => API.patch(`/bookings/${id}/guest`, data).then(res => { invalidateCache(); return res; }),
  // Meeting notes — sticky-note style notes taken during the session.
  // Each booking owns an array of coloured notes the host can CRUD.
  addNote: (id, data) => API.post(`/bookings/${id}/notes`, data).then(res => { invalidateCache(); return res; }),
  updateNote: (id, noteId, data) => API.patch(`/bookings/${id}/notes/${noteId}`, data).then(res => { invalidateCache(); return res; }),
  deleteNote: (id, noteId) => API.delete(`/bookings/${id}/notes/${noteId}`).then(res => { invalidateCache(); return res; }),
  // Structured session note — single long-form record per booking, used
  // for SOAP / coaching / summary write-ups. AI generation is on aiAPI
  // (generateSessionNote); these two persist whatever the host saves.
  saveSessionNote: (id, data) => API.put(`/bookings/${id}/session-note`, data).then(res => { invalidateCache(); return res; }),
  deleteSessionNote: (id) => API.delete(`/bookings/${id}/session-note`).then(res => { invalidateCache(); return res; }),
};

// ============================
// USER NOTES API
// ============================
// Personal notes kept outside of any booking — the floating pad in the
// dashboard. Separate endpoint surface so pages that only need booking
// notes don't need to know about this one.
export const userNoteAPI = {
  getAll: () => API.get('/user-notes'),
  create: (data) => API.post('/user-notes', data),
  update: (id, data) => API.patch(`/user-notes/${id}`, data),
  delete: (id) => API.delete(`/user-notes/${id}`),
};

// ============================
// SUBSCRIPTION API
// ============================
export const subscriptionAPI = {
  getPlans: () => API.get('/subscriptions/plans'),
  getSubscription: () => API.get('/subscriptions'),
  createCheckout: (data) => API.post('/subscriptions/checkout', data),
  verifyCheckout: (sessionId) => API.get(`/subscriptions/verify?sessionId=${sessionId}`),
  billingPortal: () => API.post('/subscriptions/billing-portal'),
  cancelSubscription: (data) => API.post('/subscriptions/cancel', data).then(res => { invalidateCache(); return res; }),
  reactivate: () => API.post('/subscriptions/reactivate'),
};

// ============================
// AVAILABILITY API
// ============================
export const availabilityAPI = {
  // duration is optional — included only when the calendar offers
  // multiple durations and the guest has picked one. Backend
  // validates against calendar.durationOptions.
  getSlots: (calendarId, date, timezone, duration) =>
    API.get('/availability/slots', {
      params: { calendarId, date, timezone, ...(duration ? { duration } : {}) }
    }),
  getDates: (calendarId, days = 30) =>
    API.get('/availability/dates', { params: { calendarId, days } }),
};

// ============================
// API FUNCTIONS
// ============================
export const availSettingsAPI = {
  getSettings: () => API.get('/availability-settings/settings'),
  getCalendar: (month, year) => API.get('/availability-settings/calendar', { params: { month, year } }),
  getHolidays: (country) => API.get('/availability-settings/holidays', { params: { country } }),
  updateHolidays: (data) => API.patch('/availability-settings/holidays', data),
  // Pre-save warning — surfaces existing future bookings that fall
  // on dates the host is about to block as holidays.
  checkHolidayConflicts: (data) => API.post('/availability-settings/holidays/check-conflicts', data),
  updateGlobalLimits: (data) => API.patch('/availability-settings/global-limits', data),
  addDateOverride: (data) => API.post('/availability-settings/date-override', data),
  removeDateOverride: (data) => API.delete('/availability-settings/date-override', { data }),
  // User-level default weekly hours (seed for new calendars)
  getDefaultAvailability: () => API.get('/availability-settings/default-availability'),
  updateDefaultAvailability: (data) => API.patch('/availability-settings/default-availability', data),
};

// Reusable named schedules (Calendly-style): create once, link many calendars.
export const scheduleAPI = {
  list: () => API.get('/schedules'),
  get: (id) => API.get(`/schedules/${id}`),
  create: (data) => API.post('/schedules', data),
  update: (id, data) => API.patch(`/schedules/${id}`, data),
  remove: (id) => API.delete(`/schedules/${id}`),
  // Link/unlink a calendar to a schedule. Pass { scheduleId } or { scheduleId: null }
  setForCalendar: (calendarId, scheduleId) =>
    API.patch(`/schedules/calendars/${calendarId}/schedule`, { scheduleId })
};

// ============================
// ANALYTICS API
// ============================
export const analyticsAPI = {
  getAnalytics: (params) => API.get('/analytics', { params }),
};

// ============================
// NOTIFICATION API
// ============================
export const notificationAPI = {
  getAll: (params) => API.get('/notifications', { params, skipToast: true }),
  getUnreadCount: () => API.get('/notifications/unread-count', { skipToast: true }),
  markAsRead: (id) => API.patch(`/notifications/${id}/read`),
  markAllAsRead: () => API.patch('/notifications/read-all'),
  delete: (id) => API.delete(`/notifications/${id}`),
  clearAll: () => API.delete('/notifications'),
};

// ============================
// AI API
// ============================
export const aiAPI = {
  chat: (data) => API.post('/ai/chat', data),
  meetingPrep: (bookingId) => API.get(`/ai/meeting-prep/${bookingId}`),
  getInsights: () => API.get('/ai/insights'),
  draftEmail: (data) => API.post('/ai/draft-email', data),
  composeBookingReply: (data) => API.post('/ai/compose-booking-reply', data),
  // AI Session Notes — host pastes/dictates raw notes from a completed
  // session; AI returns a structured write-up (simple summary / SOAP /
  // coaching format). Generation only — saving happens via bookingAPI.
  generateSessionNote: (data) => API.post('/ai/generate-session-note', data),
  // AI Agent (Beta) — tool-calling chat that can fetch live data from
  // the user's calendars / bookings / customers. Distinct from `chat`
  // (which is a plain conversational chatbot with no tool access).
  // Staging-only until 3-week validation completes; never call from
  // prod paths until the feature flag is enabled.
  agentChat: (data) => API.post('/ai/agent-chat', data),
};

// ============================
// INTEGRATION API
// ============================
export const integrationAPI = {
  getStatus: () => API.get('/integrations/status'),
  getZoomAuthUrl: () => API.get('/integrations/zoom/auth-url'),
  disconnectZoom: () => API.post('/integrations/zoom/disconnect'),
  getGoogleAuthUrl: () => API.get('/integrations/google/auth-url'),
  disconnectGoogle: () => API.post('/integrations/google/disconnect'),
  // 2-way Calendar Sync — list user's Google calendars + save which
  // ones to use for conflict-checks vs the destination calendar.
  listGoogleCalendars: () => API.get('/integrations/google/calendars'),
  updateGoogleSyncSettings: (data) => API.patch('/integrations/google/sync-settings', data),
  // Microsoft 365 (Outlook + Teams) — same Azure AD app as social
  // login but extended scopes. Same API shape as Google sync.
  getMicrosoftAuthUrl: () => API.get('/integrations/microsoft/auth-url'),
  disconnectMicrosoft: () => API.post('/integrations/microsoft/disconnect'),
  listMicrosoftCalendars: () => API.get('/integrations/microsoft/calendars'),
  updateMicrosoftSyncSettings: (data) => API.patch('/integrations/microsoft/sync-settings', data),
};

// ============================
// TOOLS API
// ============================
export const toolsAPI = {
  getEmbedCode: (calendarId, type) => API.get(`/tools/embed/${calendarId}`, { params: { type } }),
  getQRCode: (calendarId) => API.get(`/tools/qr/${calendarId}`),
  exportCSV: (params) => API.get('/tools/export/bookings', { params, responseType: 'blob' }),
  getContacts: (params) => API.get('/tools/contacts', { params }),
  getContactHistory: (email) => API.get(`/tools/contacts/${encodeURIComponent(email)}`),
  createBookingCheckout: (data) => API.post('/tools/booking-checkout', data),
  verifyBookingPayment: (sessionId) => API.get(`/tools/booking-payment/verify`, { params: { sessionId } }),
  verifyEmail: (email) => API.post('/tools/verify-email', { email }, { skipToast: true }),
};

// ============================
// CONTACT API
// ============================
export const contactAPI = {
  send: (data) => API.post('/contact', data),
  // In-app issue reports from the floating "Report Issue" widget. Backend
  // auto-fills user identity from the auth token, so the frontend only has
  // to ship the description + page context.
  reportIssue: (data) => API.post('/contact/issue', data),
};

// ============================
// NEWSLETTER API
// ============================
export const newsletterAPI = {
  // Public — landing page signup form
  subscribe: (data) => API.post('/newsletter/subscribe', data),
  // Public unsubscribe flow (token-based, no auth)
  lookupByToken: (token) => API.get(`/newsletter/by-token/${token}`),
  unsubscribe: (token) => API.post(`/newsletter/unsubscribe/${token}`),
  resubscribe: (token) => API.post(`/newsletter/resubscribe/${token}`),
  // Admin only
  listSubscribers: (params) => API.get('/newsletter/subscribers', { params }),
  exportCSV: () => API.get('/newsletter/export', { responseType: 'blob' }),
};

// ============================
// ADMIN API
// ============================
export const adminAPI = {
  getStats: () => API.get('/admin/stats'),
  getUsers: (params) => API.get('/admin/users', { params }),
  getUserById: (id) => API.get(`/admin/users/${id}`),
  banUser: (id, reason) => API.post(`/admin/users/${id}/ban`, { reason }),
  unbanUser: (id) => API.post(`/admin/users/${id}/unban`),
  getOnboardingInsights: () => API.get('/users/onboarding-insights'),
};

// ============================
// TWO-FACTOR AUTH API
// ============================
export const twoFactorAPI = {
  getStatus: () => API.get('/2fa/status'),
  setup: () => API.post('/2fa/setup'),
  verify: (code) => API.post('/2fa/verify', { code }),
  disable: (password) => API.post('/2fa/disable', { password }),
  verifyLogin: (pendingToken, code) => API.post('/2fa/verify-login', { pendingToken, code }),
};

// ============================
// TEAM MANAGEMENT
// ============================
export const teamAPI = {
  getMyTeam: () => API.get('/teams/me', { skipToast: true }),
  create: (data) => API.post('/teams', data),
  update: (teamId, data) => API.patch(`/teams/${teamId}`, data),
  remove: (teamId) => API.delete(`/teams/${teamId}`),
  getAuditLog: (teamId, params) => API.get(`/teams/${teamId}/audit-log`, { params }),
  invite: (teamId, data) => API.post(`/teams/${teamId}/invite`, data),
  bulkInvite: (teamId, data) => API.post(`/teams/${teamId}/invite/bulk`, data),
  suggestEmails: (teamId, q) => API.get(`/teams/${teamId}/suggest-emails`, { params: { q }, skipToast: true }),
  revokeInvitation: (teamId, invitationId) => API.delete(`/teams/${teamId}/invitations/${invitationId}`),
  changeRole: (teamId, memberId, role) => API.patch(`/teams/${teamId}/members/${memberId}/role`, { role }),
  suspendMember: (teamId, memberId) => API.patch(`/teams/${teamId}/members/${memberId}/suspend`),
  reactivateMember: (teamId, memberId) => API.patch(`/teams/${teamId}/members/${memberId}/reactivate`),
  removeMember: (teamId, memberId) => API.delete(`/teams/${teamId}/members/${memberId}`),
  exportMembers: (teamId) => API.get(`/teams/${teamId}/members/export`, { responseType: 'blob' }),
  transferOwnership: (teamId, newOwnerId, password) => API.post(`/teams/${teamId}/transfer-ownership`, { newOwnerId, password }),
  leaveTeam: (teamId) => API.post(`/teams/${teamId}/leave`),
  previewInvite: (token) => API.post('/teams/invite/preview', { token }, { skipToast: true }),
  acceptInvite: (token) => API.post('/teams/invite/accept', { token }),
};

// ============================
// ONBOARDING
// ============================
export const onboardingAPI = {
  getStatus: () => API.get('/users/onboarding', { skipToast: true }),
  save: (data) => API.post('/users/onboarding', data),
  skip: () => API.post('/users/onboarding/skip', {}, { skipToast: true }),
  reset: () => API.post('/users/onboarding/reset'),
  setWelcomeTrade: (trade) => API.post('/users/welcome-trade', { trade }, { skipToast: true }),
  dismissProfileBanner: () => API.post('/users/profile-banner/dismiss', {}, { skipToast: true }),
  dismissChecklist: () => API.post('/users/onboarding/dismiss-checklist', {}, { skipToast: true }),
  getInsights: () => API.get('/users/onboarding-insights'),
};

// ============================
// CUSTOM DOMAINS
// ============================
export const customDomainAPI = {
  list: () => API.get('/custom-domains'),
  add: (domain) => API.post('/custom-domains', { domain }),
  verify: (id) => API.post(`/custom-domains/${id}/verify`),
  remove: (id) => API.delete(`/custom-domains/${id}`),
};

// ============================
// API KEYS (Developer API)
// ============================
export const apiKeyAPI = {
  list: () => API.get('/api-keys'),
  create: (data) => API.post('/api-keys', data),
  revoke: (id) => API.patch(`/api-keys/${id}/revoke`),
  remove: (id) => API.delete(`/api-keys/${id}`),
};

// ============================
// WEBHOOKS (outgoing)
// ============================
export const webhookAPI = {
  list: () => API.get('/webhooks'),
  create: (data) => API.post('/webhooks', data),
  update: (id, data) => API.patch(`/webhooks/${id}`, data),
  remove: (id) => API.delete(`/webhooks/${id}`),
  rotateSecret: (id) => API.post(`/webhooks/${id}/rotate`),
  test: (id) => API.post(`/webhooks/${id}/test`),
  deliveries: (id) => API.get(`/webhooks/${id}/deliveries`),
};

// ============================
// PUSH NOTIFICATIONS API
// ============================
export const pushAPI = {
  getPublicKey: () => API.get('/push/public-key', { skipAuth: true, skipToast: true }),
  getStatus: () => API.get('/push/status', { skipToast: true }),
  subscribe: (data) => API.post('/push/subscribe', data, { skipToast: true }),
  unsubscribe: (data) => API.post('/push/unsubscribe', data, { skipToast: true }),
};

export default API;
